import type { DialogRootProps } from '@chakra-ui/react';
import { slice, map, find, uniqBy, filter, size } from 'lodash';
import {
  Flex,
  Span,
  Stack,
  Portal,
  HStack,
  Checkbox,
  CheckboxGroup,
  Fieldset,
  For,
  Center,
  Button,
  Tag,
  VStack,
  Box,
  Text,
} from '@chakra-ui/react';
import {
  Loading,
  SearchInput,
  Avatar,
  ChatAvatar,
  ChatMetaHeader,
  ButtonGray,
  RippleEffect,
} from '@halo/ui-chat-react';
import { Dialog, toaster } from '@halo/ui-react';
import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import type { PropsWithChildren } from 'react';
import type { User } from '@halo/chat-sdk-react';
import { useAppStore, GroupStatusEnum } from '@halo/chat-sdk-react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { safeJsonParse } from '@halo/utils';
import { useRefs } from '@halo/hooks';
import { useDebounceFn } from 'ahooks';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { t } from 'i18next';
import { isSessionCategory, SessionCategoryPrefix, useStaticAppStore } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import ArrowDownIcon from '@/assets/images/common/svg/arrow_down.svg?react';
import Paths from '@/configs/router/paths';
import { type SearchResults, SearchTabs } from './helpers';
import MessageDatePicker from './MessageDatePicker';
import MessageHighlighter from './MessageHelper';
import { UserListHeader, type ResultContactsProps } from './ResultContacts';

interface ResultMessagesProps {
  searchValue: string;
  searchResults: SearchResults;
}

interface DialogProps {
  open: boolean;
  text: string;
  title: string;
  cancelText?: string;
  okText?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xs' | 'cover' | 'full';
  bg?: string;
  displayTrigger: boolean;
  onOk: () => void;
  onClose: () => void;
  setOpen: (open: boolean) => void;
}
interface UserSelectProps {
  users: {
    name: string;
    group_name?: string;
    avatar: string;
    chat_id: string;
    username: string;
    member_num?: number;
    state: any;
    isGroup: boolean;
    owner_id?: string;
  }[];
  value: string[] | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onValueChange: (data: string[] | null) => void;
}
interface MessageListProps {
  list: Record<string, any>[];
  searchKey: string;
}

const queryGroup = async (client: any, chat_id: string) => {
  let _result: Record<string, unknown> = {};
  const _res = await client?.Api?.session.getGroupInfo(chat_id);
  _result = _res;
  return _result;
};
const TargetDialog = (props: PropsWithChildren<DialogProps> & DialogRootProps) => {
  const {
    displayTrigger,
    open,
    text,
    title,
    children,
    disabled,
    cancelText = t('buttons.cancel'),
    okText = t('buttons.confirm'),
    size = 'lg',
    bg,
    onOk,
    setOpen,
    onClose,
    ...rest
  } = props;
  return (
    <Dialog.Root
      placement="top"
      size={size}
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      closeOnInteractOutside={false}
      {...rest}
    >
      <Dialog.Trigger asChild>
        {displayTrigger && (
          <Flex
            w="max-content"
            bg="bg"
            _hover={{
              bg: 'grey.64',
            }}
            borderRadius="lg"
            p="2"
            align="center"
            gap="2.5"
            justify="center"
            cursor="pointer"
            color="fg"
            fontWeight="medium"
            lineHeight="1"
            fontSize="xs"
          >
            <Span>{text}</Span>
            <ArrowDownIcon />
          </Flex>
        )}
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner pointerEvents="none">
          <Dialog.Content overflow="hidden" bg={bg}>
            <Dialog.Header
              p="4"
              alignItems="center"
              fontSize="md"
              fontWeight="semibold"
              color="fg"
            >
              {title}
            </Dialog.Header>
            <Dialog.Body p="4">{children}</Dialog.Body>
            <Dialog.Footer>
              <ButtonGray
                borderRadius="xl"
                onClick={() => {
                  setOpen(false);
                  onClose();
                }}
              >
                {cancelText}
              </ButtonGray>
              <Button
                borderRadius="xl"
                bg="brand"
                disabled={disabled}
                onClick={() => {
                  onOk();
                  setOpen(false);
                }}
              >
                {okText}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
const SelectMembers = memo(function SelectMembers(props: UserSelectProps) {
  const { users, value, open, setOpen, onValueChange } = props;
  const [word, setWord] = useState<string>('');
  const [selected, setSelected] = useState<string[]>([]);
  const filterList = useMemo(() => {
    const key = word.toLowerCase();
    return users?.filter((item) => {
      const isGroup = isSessionCategory(item.chat_id, SessionCategoryPrefix.Group);
      return isGroup
        ? item?.group_name?.toLowerCase()?.includes(key)
        : item?.name?.toLowerCase()?.includes(key) ||
            item?.username?.toLowerCase()?.includes(key);
    });
  }, [word, users]);
  useEffect(() => {
    setSelected(value ?? []);
  }, [value]);

  return (
    <TargetDialog
      displayTrigger={!selected?.length}
      open={open}
      title={t('filter.fromTo')}
      text={t('filter.from')}
      okText={t('buttons.select')}
      setOpen={(open) => {
        setOpen(open);
      }}
      onOk={() => {
        onValueChange(selected);
      }}
      onClose={() => {
        setSelected(value ?? []);
      }}
    >
      <HStack
        w="full"
        borderRadius="lg"
        border="1px solid"
        borderColor="border"
        h="500px"
        px="4"
        gap="4"
        align="flex-start"
      >
        <Stack
          flex={1}
          h="full"
          py="4"
          pr="4"
          borderRight="1px solid"
          borderColor="border"
          overflow="hidden"
        >
          <SearchInput value={word} onChange={setWord} />
          <Stack className="List" flex={1} overflow="auto">
            <Fieldset.Root>
              <CheckboxGroup
                value={selected}
                onValueChange={(value) => {
                  setSelected(value);
                }}
              >
                <Fieldset.Content gap="0">
                  <For each={filterList}>
                    {(item, index) => {
                      const {
                        isGroup,
                        chat_id,
                        owner_id,
                        group_name,
                        name,
                        username,
                        avatar,
                        member_num,
                      } = item;

                      return (
                        <Checkbox.Root
                          key={isGroup ? chat_id : owner_id}
                          value={isGroup ? chat_id : owner_id}
                          size="sm"
                        >
                          <Checkbox.HiddenInput />
                          <RippleEffect w="full" enabled>
                            <Box
                              className="UserItem"
                              px="2"
                              pt="2"
                              w="full"
                              overflow="hidden"
                              _hover={{
                                bg: 'bg.subtle',
                                cursor: 'pointer',
                              }}
                            >
                              <HStack gap="2.5" flex="1" align="flex-start" fontSize="xs">
                                <Center h="3em">
                                  <Checkbox.Control
                                    _checked={{
                                      bg: 'brand.fg',
                                      color: 'bg',
                                      borderColor: 'brand.solid',
                                    }}
                                  />
                                </Center>
                                <Avatar size="sm" src={avatar} activeShake={false} />
                                <VStack
                                  flex="1"
                                  align="flex-start"
                                  justify="center"
                                  pb="2"
                                  gap="1"
                                  h="3em"
                                  lineHeight="1"
                                  borderBottom={index === users.length - 1 ? '0' : '1px'}
                                  borderStyle="solid"
                                  borderColor="border"
                                  boxSizing="content-box"
                                  w="full"
                                  css={{
                                    '& > div': {
                                      w: 'full',
                                    },
                                  }}
                                >
                                  <ChatMetaHeader
                                    sessionName={isGroup ? group_name! : name}
                                    name={username}
                                    isGroup={isGroup}
                                    updateAtDisplay=""
                                  />
                                  <Text
                                    fontWeight="medium"
                                    lineHeight="1"
                                    color="fg.muted"
                                    truncate
                                  >
                                    {isGroup
                                      ? `${member_num} ${t('chat.groupMembersText')}`
                                      : username}
                                  </Text>
                                </VStack>
                              </HStack>
                            </Box>
                          </RippleEffect>
                        </Checkbox.Root>
                      );
                    }}
                  </For>
                </Fieldset.Content>
              </CheckboxGroup>
            </Fieldset.Root>
          </Stack>
        </Stack>
        <Stack flex={1} py="4" fontSize="xs" color="fg" fontWeight="medium">
          <Center h="3em" justifyContent="flex-start">
            <Span>{t('filter.selectedMembers', { count: selected.length })}</Span>
          </Center>
        </Stack>
      </HStack>
    </TargetDialog>
  );
});
const SelectDate = (props: Omit<UserSelectProps, 'users'>) => {
  const { value, open, setOpen, onValueChange } = props;
  const [times, setTimes] = useState<string[] | null>(null);
  useEffect(() => {
    setTimes(value);
  }, [value]);

  return (
    <TargetDialog
      displayTrigger={!(times && times?.[0] && times?.[1])}
      open={open}
      size="xs"
      title={t('filter.selectDate')}
      text={t('filter.date')}
      okText={t('buttons.save')}
      bg="bg.subtle"
      onOk={() => {
        onValueChange(times);
      }}
      onClose={() => {
        setTimes(value);
      }}
      setOpen={(open) => setOpen(open)}
    >
      <MessageDatePicker value={times} onChange={(values) => setTimes(values)} />
    </TargetDialog>
  );
};
const MessageList = (props: MessageListProps) => {
  const { list, searchKey } = props;
  const { client } = useAppStore(useShallowEnhance(['client']));
  const { sessions } = useStaticAppStore();
  const { setGlobalSearchIsOpen } = useActionsStore(useShallowEnhance(['setGlobalSearchIsOpen']));
  const navigate = useNavigate();
  const handleContactClick = useCallback(
    async (contact: Record<string, string>) => {
      const { chat_id, user_id, gid } = contact;
      const content = find(sessions, { chat_id })?.last_message?.payload?.content;
      let dispose = false;
      const isGroup = isSessionCategory(chat_id, SessionCategoryPrefix.Group);
      if (isGroup) {
        const result = await queryGroup(client, gid);
        if (!result || result?.status == GroupStatusEnum.DISBAND) {
          dispose = true;
        }
      } else {
        const result = user_id && (await client?.Api.user.queryUserInfoGlobal(user_id));
        if (!result?.name) {
          dispose = true;
        }
      }
      if (dispose && !content) {
        toaster.error({
          title: isGroup ? t('tips.groupDisbanded') : t('tips.accountDeleted'),
        });
        return;
      }
      setGlobalSearchIsOpen(false);
      navigate(Paths.MESSAGES_CHATS + '/' + chat_id, {
        state: {
          search: contact,
          dispose,
          isGroup,
        },
      });
    },
    [client, sessions, navigate, setGlobalSearchIsOpen]
  );
  return (
    <For each={list}>
      {(session, index) => {
        const {
          name,
          chat_id,
          cli_msg_id,
          payload,
          username,
          created_at,
          group_name,
          avatar,
          expire_duration,
          userInfo,
        } = session;
        const isGroup = isSessionCategory(chat_id, SessionCategoryPrefix.Group);
        return (
          <Box
            key={cli_msg_id}
            px="2"
            pt="2"
            w="full"
            _hover={{
              bg: 'bg.subtle',
            }}
            cursor="pointer"
            onClick={() => {
              handleContactClick(session);
            }}
          >
            <HStack gap="4" align="center">
              {isGroup ? (
                <Avatar size="sm" src={avatar} name={group_name} mt="-2" />
              ) : (
                <ChatAvatar
                  src={avatar}
                  duration={expire_duration}
                  activeShake={!isGroup}
                  name={name}
                  mt="-2"
                />
              )}

              <VStack
                flex="1"
                align="flex-start"
                justify="center"
                pb="2"
                gap="1"
                lineHeight="1"
                fontSize="xs"
                borderBottom={index === list.length - 1 ? '0' : '1px'}
                borderStyle="solid"
                borderColor="border"
                boxSizing="content-box"
                overflow="hidden"
                w="full"
                css={{
                  '& > div': {
                    w: 'full',
                  },
                }}
              >
                <ChatMetaHeader
                  sessionName={name || group_name}
                  name={username}
                  isGroup={isGroup}
                  updateAtDisplay={dayjs(created_at).format('MM-DD HH:mm')}
                />
                {isGroup && (
                  <Text fontWeight="medium" truncate pr="1" fontSize="xs" w="full">
                    {userInfo?.name}
                  </Text>
                )}

                <MessageHighlighter
                  text={payload?.content}
                  searchKeyword={searchKey}
                  highlightColor="#703EFF"
                />
              </VStack>
            </HStack>
          </Box>
        );
      }}
    </For>
  );
};
export const ResultTopMessage = (props: ResultContactsProps) => {
  const { searchResults, searchValue, setActive } = props;
  const { messagesData } = searchResults;
  const { client } = useAppStore();
  const [messageList, setMessageList] = useState<Record<string, any>[]>([]);
  const { run: getContactInfo } = useDebounceFn(
    async (msgs: any[]) => {
      msgs = map(msgs, (item) => ({
        ...item,
        isGroup: isSessionCategory(item.chat_id, SessionCategoryPrefix.Group),
      }));
      const sessions: any = await client?.Api?.contacts.getSessionsById(
        map(msgs, (item) => item.chat_id)
      );
      const groups = uniqBy(
        filter(msgs, (item) => item.isGroup),
        (user) => `${user.chat_id}-${user.owner_id}`
      );
      const users = uniqBy(
        filter(msgs, (item) => !item.isGroup),
        'owner_id'
      );
      const receives: Record<string, any>[] = [];
      for (const item of groups.concat(users)) {
        const { owner_id, chat_id, isGroup } = item;

        if (isGroup) {
          const res = await queryGroup(client, chat_id);
          const userProfile: User = await new Promise((resolve) => {
            client?.Api?.user?.getUserProfiles(owner_id, resolve);
          });
          const _result = {
            ...item,
            ...res,
            userInfo: userProfile,
            isGroup,
          };
          receives.push(_result);
        } else {
          const userProfile: object = await new Promise((resolve) => {
            client?.Api?.user?.getUserProfiles(owner_id, resolve);
          });
          const _result = {
            ...item,
            ...userProfile,
            isGroup,
          };
          receives.push(_result);
        }
      }
      const _group_receives = filter(receives, (item) => item.isGroup);
      const _single_receives = filter(receives, (item) => !item.isGroup);
      const newInfo = map(msgs, (item) => {
        const isGroup = item?.isGroup;
        const _obj = isGroup
          ? find(_group_receives, { gid: item.chat_id, owner_id: item.owner_id })
          : find(_single_receives, { owner_id: item.owner_id });
        const expire_duration = sessions?.[item.chat_id]?.expire_duration;
        return {
          ...item,
          ..._obj,
          cli_msg_id: item?.cli_msg_id,
          payload: safeJsonParse(item?.payload, {}),
          expire_duration,
        };
      });
      setMessageList(newInfo);
    },
    {
      wait: 500,
    }
  );
  useEffect(() => {
    getContactInfo(slice(messagesData ?? [], 0, 3));
  }, [messagesData, getContactInfo]);
  if (!messagesData?.length) return null;
  return (
    <Box>
      <Box fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
        <UserListHeader
          title={t('settings.messages')}
          list={messagesData ?? []}
          setActive={() => setActive(SearchTabs.Messages)}
        />
      </Box>
      <Box className="List" bg="bg" p="1" borderRadius="lg">
        <Stack h="full" overflow="auto" gap="2">
          <MessageList list={slice(messageList ?? [], 0, 3)} searchKey={searchValue} />
        </Stack>
      </Box>
    </Box>
  );
};
export const ResultMessages = ({ searchResults, searchValue }: ResultMessagesProps) => {
  const { messagesData } = searchResults;
  const { client } = useAppStore(useShallowEnhance(['client']));
  const [messageList, setMessageList] = useState<Record<string, any>[]>([]);
  const [contactsUsers, setContacts] = useState<any[]>([]);
  const [selectUsers, setSelectUsers] = useState<string[]>();
  const [selectOpen, setSelectOpen] = useState(false);
  const [times, setTimes] = useState<string[] | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const firstSelectUser: any = useMemo(
    () =>
      contactsUsers?.find((item: any) => {
        const _first = selectUsers?.[0];
        const isGroup = isSessionCategory(_first, SessionCategoryPrefix.Group);
        if (isGroup) {
          return item?.chat_id === _first;
        } else {
          if (!item.isGroup) {
            return item?.owner_id === _first;
          }
        }
      }) ?? {},
    [selectUsers, contactsUsers]
  );
  const { run: getContactInfo } = useDebounceFn(
    async (msgs: any[], update = false) => {
      msgs = map(msgs, (item) => ({
        ...item,
        isGroup: isSessionCategory(item.chat_id, SessionCategoryPrefix.Group),
      }));
      setLoading(true);
      const sessions: any = await client?.Api?.contacts.getSessionsById(
        map(msgs, (item) => item.chat_id)
      );
      const groups = uniqBy(
        filter(msgs, (item) => item.isGroup),
        (user) => `${user.chat_id}-${user.owner_id}`
      );
      const users = uniqBy(
        filter(msgs, (item) => !item.isGroup),
        'owner_id'
      );
      const receives: Record<string, any>[] = [];
      for (const item of groups.concat(users)) {
        const { owner_id, chat_id, isGroup } = item;

        if (isGroup) {
          const res = await queryGroup(client, chat_id);
          const userProfile: User = await new Promise((resolve) => {
            client?.Api?.user?.getUserProfiles(owner_id, resolve);
          });
          const _result = {
            ...item,
            ...res,
            userInfo: userProfile,
            isGroup,
          };
          receives.push(_result);
        } else {
          const userProfile: object = await new Promise((resolve) => {
            client?.Api?.user?.getUserProfiles(owner_id, resolve);
          });
          const _result = {
            ...item,
            ...userProfile,
            isGroup,
          };
          receives.push(_result);
        }
      }
      const _group_receives = filter(receives, (item) => item.isGroup);
      const _single_receives = filter(receives, (item) => !item.isGroup);
      const newInfo = map(msgs, (item) => {
        const isGroup = item?.isGroup;
        const _obj = isGroup
          ? find(_group_receives, { gid: item.chat_id, owner_id: item.owner_id })
          : find(_single_receives, { owner_id: item.owner_id });
        const expire_duration = sessions?.[item.chat_id]?.expire_duration;
        return {
          ...item,
          ..._obj,
          cli_msg_id: item?.cli_msg_id,
          payload: safeJsonParse(item?.payload, {}),
          expire_duration,
        };
      });

      if (update) {
        const _groups_contacts = uniqBy(_group_receives, 'chat_id');
        const _users_contacts = uniqBy(_single_receives, 'owner_id');
        setContacts([..._groups_contacts, ..._users_contacts]);
      }
      setMessageList(newInfo);
      setLoading(false);
    },
    {
      wait: 500,
    }
  );

  const { run: getMessages } = useDebounceFn(
    async () => {
      if (!searchValue) return;
      const chat_ids = uniqBy(
        map(selectUsers || [], (item) => {
          if (isSessionCategory(item, SessionCategoryPrefix.Group)) {
            return item;
          }
          return filter(contactsUsers, (user) => !user.isGroup && user.owner_id === item).map(
            (user) => user.chat_id
          );
        }).flat(),
        (item) => item
      );
      const result: any = await client?.Api?.messages.messagesSearchByKey(
        searchValue,
        chat_ids,
        times?.[0] ? dayjs(times?.[0]).valueOf() : 0,
        times?.[1] ? dayjs(times?.[1]).valueOf() : 0,
        0,
        100
      );
      getContactInfo(result?.message || []);
    },
    { wait: 500 }
  );

  const refs = useRefs({
    getMessages,
    getContactInfo,
  });

  useEffect(() => {
    const { getContactInfo } = refs.current;
    setContacts([]);
    getContactInfo(messagesData ?? [], true);
  }, [messagesData, refs]);

  useEffect(() => {
    const { getMessages } = refs.current;
    getMessages();
  }, [selectUsers, times, refs]);

  useEffect(() => {
    setSelectUsers(undefined);
    setTimes(null);
  }, [searchValue]);
  return (
    <Loading spin={loading} display={size(messageList) > 0}>
      <Flex className="fadeIn" gap="2" direction="column" h="full">
        <HStack gap="3">
          {selectUsers && selectUsers.length > 0 && (
            <Tag.Root fontSize="xs" h="2.33em" borderRadius="lg">
              <Tag.Label
                cursor="pointer"
                onClick={() => {
                  setSelectOpen(true);
                }}
              >
                <HStack fontWeight="medium">
                  <Span>{t('filter.from')}</Span>
                  <Avatar w="1.3em" h="1.3em" src={firstSelectUser?.avatar} />
                  {selectUsers.length > 1 && <Span> +{selectUsers.length - 1}</Span>}
                </HStack>
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  cursor="pointer"
                  onClick={() => {
                    setSelectUsers([]);
                  }}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}
          <SelectMembers
            users={contactsUsers ?? []}
            onValueChange={(value) => setSelectUsers(value || [])}
            value={selectUsers || []}
            open={selectOpen}
            setOpen={setSelectOpen}
          />
          {times && times?.[0] && times?.[1] && (
            <Tag.Root fontSize="xs" h="2.33em" borderRadius="lg">
              <Tag.Label
                cursor="pointer"
                onClick={() => {
                  setDateOpen(true);
                }}
              >
                <HStack fontWeight="medium">
                  <Span>{t('filter.date')}</Span>
                  <Span>
                    {map(times, (time) => dayjs(time).format('YYYY.MM.DD')).join(' - ')}
                  </Span>
                </HStack>
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  cursor="pointer"
                  onClick={() => {
                    setTimes(null);
                  }}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}
          <SelectDate
            onValueChange={(value) => setTimes(value)}
            value={times}
            open={dateOpen}
            setOpen={setDateOpen}
          />
        </HStack>
        <Stack bg="bg" borderRadius="lg" p="2" overflow="auto">
          <Stack maxH="full" overflow="auto" gap="2">
            <MessageList list={messageList ?? []} searchKey={searchValue} />
          </Stack>
        </Stack>
      </Flex>
    </Loading>
  );
};
